/*****************************************************************
 *  ZOHO BRIDGE PANEL — ONE RECORD CARD IN THE WAITING PILE        *
 *  File: apps/web/src/pages/bridge/InboxCard.js                   *
 *  What it does: shows one Zoho record by NAME: what Zoho says    *
 *  (every Zoho field for a full-field record), what would change  *
 *  in Butler, who it looks like (contacts), its Zoho contacts     *
 *  (properties), and the Approve / Reject buttons with a "why"    *
 *  box. On an ambiguous contact, or a property with no customer,  *
 *  the person picks the customer right here (defect 379).         *
 *  Reads: nothing itself.  Writes: approve / reject through       *
 *  bridgeApi.js, only when a person confirms.                     *
 *****************************************************************/

/*****************************************************************
 *  TABLE OF CONTENTS                                              *
 *  SECTION 1 — THE SHORT FACTS                                    *
 *  SECTION 2 — EVERY ZOHO FIELD (full-field records)              *
 *  SECTION 3 — THE "WHAT WOULD CHANGE" LIST                       *
 *  SECTION 4 — THE MATCH LINE (contacts)                          *
 *  SECTION 5 — THE CONTACTS ON A PROPERTY                         *
 *  SECTION 6 — PICKING THE CUSTOMER (defect 379)                  *
 *  SECTION 7 — THE CARD                                           *
 *****************************************************************/

import React, { useState } from 'react';
import { BridgeIcon } from './bridgeIcons';
import { BRIDGE_WORDS, KIND_NAMES, MATCH_NAMES } from './bridgeWords';
import { StatusChip, ToneChip, WhyBox, formatWhen, formatMoney } from './bridgeParts';
import { approveRecord, rejectRecord, searchButlerCustomers } from './bridgeApi';

// The statuses a person can still decide on (same list as the server).
const DECIDABLE_STATUSES = ['proposed', 'failed', 'waiting'];

// The icon each kind wears.
const KIND_ICONS = {
  books_item: 'tag',
  crm_contact: 'people',
  crm_property: 'home',
};

/*****************************************************************
 *  SECTION 1 — THE SHORT FACTS                                    *
 *****************************************************************/

/* ---- listShortFacts(record) -- [label, value] pairs worth showing ---- */
function listShortFacts(record) {
  const data = record.data || {};

  if (record.kind === 'books_item') {
    return [
      ['Price', formatMoney(data.rate)],
      ['Unit', data.unit],
      ['Active?', data.status === 'inactive' ? 'No (inactive in Zoho)' : 'Yes'],
      ['Type', data.productType],
      ['Taxable?', data.taxable ? 'Yes' : 'No'],
      ['Description', data.description],
    ].filter((onePair) => Boolean(onePair[1]));
  }

  if (record.kind === 'crm_property') {
    return [
      ['Address', record.addressNormalized || data.name],
      ['Zip', data.zip],
      ['County', data.county],
    ].filter((onePair) => Boolean(onePair[1]));
  }

  const streetLine = [data.street, data.city, data.state, data.zip].filter(Boolean).join(', ');

  return [
    ['Email', data.email],
    ['Phone', data.phone],
    ['Mobile', data.mobile],
    ['Address', streetLine],
    ['Account', data.accountName],
  ].filter((onePair) => Boolean(onePair[1]));
}

/* ---- FactList({ facts }) -- a two-column list ---- */
function FactList({ facts }) {
  return (
    <dl className="zb-facts">
      {facts.map((onePair) => (
        <div className="zb-fact" key={onePair[0]}>
          <dt>{onePair[0]}</dt>
          <dd>{onePair[1]}</dd>
        </div>
      ))}
    </dl>
  );
}

/*****************************************************************
 *  SECTION 2 — EVERY ZOHO FIELD (full-field records)              *
 *****************************************************************/

/* ---- groupFieldLines(fieldLines) -- [{section, lines}] in order ---- */
function groupFieldLines(fieldLines) {
  const groups = [];

  for (const oneLine of fieldLines) {
    const lastGroup = groups[groups.length - 1];

    if (lastGroup && lastGroup.section === oneLine.section) {
      lastGroup.lines.push(oneLine);
    } else {
      groups.push({ section: oneLine.section, lines: [oneLine] });
    }
  }

  return groups;
}

/* ---- AllZohoFields({ record }) ---------------------------------
   A "Full fields" badge, and a button that opens every Zoho field
   in Zoho's own sections and order.                            */
function AllZohoFields({ record }) {
  const [isOpen, setIsOpen] = useState(false);
  const fieldLines = Array.isArray(record.fieldsForScreen) ? record.fieldsForScreen : [];
  const isFullFields = record.schemaVersion === 2;

  if (record.kind === 'books_item') {
    return null;
  }

  if (!isFullFields) {
    return (
      <div className="zb-row zb-row-tight">
        <ToneChip tone="neutral" icon="alert" word={BRIDGE_WORDS.thinFields} />
      </div>
    );
  }

  return (
    <div className="zb-block">
      <div className="zb-row zb-row-tight">
        <ToneChip tone="ok" icon="stack" word={BRIDGE_WORDS.fullFields + ' (' + fieldLines.length + ')'} />
        {record.historyCount > 0 ? (
          <span className="zb-small">{record.historyCount} {BRIDGE_WORDS.copiedEarlier}</span>
        ) : null}
        <button
          type="button"
          className="zb-button zb-button-plain"
          aria-expanded={isOpen}
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? BRIDGE_WORDS.hideZohoFields : BRIDGE_WORDS.allZohoFields}
        </button>
      </div>

      {isOpen ? (
        <div className="zb-fieldlist">
          {groupFieldLines(fieldLines).map((oneGroup) => (
            <div key={oneGroup.section} className="zb-fieldgroup">
              <h5 className="zb-subhead">{oneGroup.section}</h5>
              <dl className="zb-facts">
                {oneGroup.lines.map((oneLine) => (
                  <div className="zb-fact" key={oneGroup.section + oneLine.label}>
                    <dt>{oneLine.label}</dt>
                    <dd>
                      {oneLine.words}
                      {oneLine.destination === 'skip' ? ' (' + BRIDGE_WORDS.goesNowhere + ')' : ''}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/*****************************************************************
 *  SECTION 3 — THE "WHAT WOULD CHANGE" LIST                       *
 *****************************************************************/

/* ---- ChangeList({ record }) -- before -> after, one line each ---- */
function ChangeList({ record }) {
  const diff = record.diff || {};
  const changes = Array.isArray(diff.changes) ? diff.changes : [];
  const warnings = Array.isArray(diff.fieldWarnings) ? diff.fieldWarnings : [];
  const isDone = record.status === 'applied';
  const heading = isDone ? BRIDGE_WORDS.whatChanged : BRIDGE_WORDS.whatChanges;
  const followUp = diff.followUp || null;

  if (record.kind === 'crm_property' && !isDone) {
    return null;
  }

  return (
    <div className="zb-block">
      <h4 className="zb-subhead">{heading}</h4>
      {diff.isNew && record.kind === 'books_item' ? (
        <p className="zb-quiet">{isDone ? BRIDGE_WORDS.wasBrandNew : BRIDGE_WORDS.brandNew}</p>
      ) : null}
      {diff.note ? <p className="zb-quiet">{diff.note}</p> : null}
      {changes.length === 0 && !diff.isNew && !diff.note ? <p className="zb-quiet">{BRIDGE_WORDS.noChanges}</p> : null}
      {changes.length > 0 ? (
        <ul className="zb-changes">
          {changes.map((oneChange) => (
            <li key={oneChange.field}>
              <strong>{oneChange.field}</strong>
              <span className="zb-before">{oneChange.before || '(blank)'}</span>
              <span aria-hidden="true"> → </span>
              <span className="zb-visually-hidden"> becomes </span>
              <span className="zb-after">{oneChange.after || '(blank)'}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {warnings.length > 0 ? (
        <div className="zb-notice zb-notice-warn">
          <BridgeIcon name="alert" />
          <span>{BRIDGE_WORDS.fieldWarnings}: {warnings.join(' · ')}</span>
        </div>
      ) : null}
      {followUp && followUp.propertiesPutIn > 0 ? (
        <p className="zb-quiet">{followUp.propertiesPutIn} {BRIDGE_WORDS.followUpPutIn}</p>
      ) : null}
    </div>
  );
}

/*****************************************************************
 *  SECTION 4 — THE MATCH LINE (contacts)                          *
 *****************************************************************/

/* ---- MatchLine({ record }) -- who this contact looks like ---- */
function MatchLine({ record }) {
  if (record.kind !== 'crm_contact') {
    return null;
  }

  const hint = record.matchHint || {};
  const matchedCustomer = record.matchedCustomer;
  const isDone = record.status === 'applied';

  if (hint.how === 'ambiguous' && !isDone) {
    return (
      <div className="zb-notice zb-notice-warn">
        <BridgeIcon name="alert" />
        <span>{hint.reason || MATCH_NAMES.ambiguous}</span>
      </div>
    );
  }

  if (!matchedCustomer) {
    return <p className="zb-quiet">{BRIDGE_WORDS.noMatch}</p>;
  }

  const matchWord = MATCH_NAMES[hint.how] || hint.how || '';
  const isMedium = hint.confidence === 'medium';

  // honest UI: once applied, the card says what IS linked, not what it looks like
  if (isDone) {
    return (
      <div className="zb-notice zb-notice-ok">
        <BridgeIcon name="link" />
        <span>{BRIDGE_WORDS.linkedToCustomer} <strong>{matchedCustomer.displayName}</strong></span>
      </div>
    );
  }

  return (
    <div className={'zb-notice ' + (isMedium ? 'zb-notice-warn' : 'zb-notice-info')}>
      <BridgeIcon name="people" />
      <span>
        {BRIDGE_WORDS.looksLike} <strong>{matchedCustomer.displayName}</strong>
        {matchWord ? ' — ' + BRIDGE_WORDS.matchedBy + ' ' + matchWord + '. ' : '. '}
        {BRIDGE_WORDS.willUpdate}
      </span>
    </div>
  );
}

/*****************************************************************
 *  SECTION 5 — THE CONTACTS ON A PROPERTY                         *
 *****************************************************************/

/* ---- PropertyContacts({ record }) -- each Zoho contact, in Butler or not ---- */
function PropertyContacts({ record }) {
  if (record.kind !== 'crm_property') {
    return null;
  }

  const linkedContacts = Array.isArray(record.linkedContacts) ? record.linkedContacts : [];

  return (
    <div className="zb-block">
      <h4 className="zb-subhead">{BRIDGE_WORDS.linkedContactsTitle}</h4>
      {linkedContacts.length === 0 ? <p className="zb-quiet">{BRIDGE_WORDS.noContactsOnProperty}</p> : null}
      <ul className="zb-list">
        {linkedContacts.map((oneContact) => (
          <li className="zb-list-row" key={oneContact.zohoId}>
            <div className="zb-list-main">
              <BridgeIcon name="people" />
              <strong>{oneContact.name || BRIDGE_WORDS.zohoContact + ' ' + oneContact.zohoId}</strong>
            </div>
            {oneContact.customerId ? (
              <ToneChip tone="ok" icon="check" word={BRIDGE_WORDS.inButlerAs + ' ' + (oneContact.butlerName || '')} />
            ) : (
              <ToneChip tone="warn" icon="clock" word={BRIDGE_WORDS.notInButlerYet} />
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/*****************************************************************
 *  SECTION 6 — PICKING THE CUSTOMER (defect 379)                  *
 *****************************************************************/

/* ---- CustomerPicker({ record, onPick, onMakeNew }) -------------
   The candidates of an ambiguous match (or a search, for a
   property with no customer), each with "Use this customer".   */
function CustomerPicker({ record, onPick, onMakeNew }) {
  const [searchText, setSearchText] = useState('');
  const [foundCustomers, setFoundCustomers] = useState([]);
  const candidates = Array.isArray(record.candidateCustomers) ? record.candidateCustomers : [];
  const isProperty = record.kind === 'crm_property';
  const pickList = candidates.concat(foundCustomers.filter((oneFound) => (
    !candidates.some((oneCandidate) => oneCandidate.id === oneFound.id)
  )));
  const searchBoxId = 'zb-pick-search-' + record.id;

  /* ---- runSearch(event) -- ask Customer Butler (its own door) ---- */
  async function runSearch(event) {
    event.preventDefault();

    const answer = await searchButlerCustomers(searchText.trim());

    setFoundCustomers(answer.data || []);
  }

  return (
    <div className="zb-drawer">
      <p className="zb-quiet">{isProperty ? BRIDGE_WORDS.chooseOwner : BRIDGE_WORDS.candidatesTitle}</p>
      <form className="zb-row" onSubmit={runSearch}>
        <label htmlFor={searchBoxId} className="zb-visually-hidden">{BRIDGE_WORDS.searchCustomers}</label>
        <input
          id={searchBoxId}
          className="zb-input"
          type="search"
          value={searchText}
          placeholder={BRIDGE_WORDS.searchCustomers}
          onChange={(event) => setSearchText(event.target.value)}
        />
        <button type="submit" className="zb-button zb-button-plain" disabled={searchText.trim().length < 2}>
          {BRIDGE_WORDS.search}
        </button>
      </form>
      <ul className="zb-list">
        {pickList.map((oneCustomer) => (
          <li className="zb-list-row" key={oneCustomer.id}>
            <div className="zb-list-main">
              <strong>{oneCustomer.displayName}</strong>
              <span className="zb-small">{oneCustomer.primaryEmail || ''} {oneCustomer.primaryPhone || ''}</span>
            </div>
            <button type="button" className="zb-button zb-button-plain" onClick={() => onPick(oneCustomer)}>
              {BRIDGE_WORDS.useThisCustomer}
            </button>
          </li>
        ))}
      </ul>
      {!isProperty ? (
        <button type="button" className="zb-button zb-button-plain" onClick={onMakeNew}>
          {BRIDGE_WORDS.makeNewCustomer}
        </button>
      ) : null}
    </div>
  );
}

/*****************************************************************
 *  SECTION 7 — THE CARD                                           *
 *****************************************************************/

/* ---- InboxCard({ record, isSelected, onToggleSelect, onDecided })
   One record. onDecided() is called after any decision so the
   tab can reload the pile and the counts.                       */
export default function InboxCard({ record, isSelected, onToggleSelect, onDecided }) {
  const [openBox, setOpenBox] = useState('');          // '', 'approve', 'reject' or 'pick'
  const [isBusy, setIsBusy] = useState(false);          // a call is on its way
  const [answerText, setAnswerText] = useState('');     // what the server said
  const [answerTone, setAnswerTone] = useState('info'); // how to colour it
  const [useOlder, setUseOlder] = useState(false);      // owner override
  const [needsOlderTick, setNeedsOlderTick] = useState(false);
  const [choice, setChoice] = useState(null);           // { chooseCustomerId, name } or { createNew }

  const canDecide = DECIDABLE_STATUSES.includes(record.status);
  const kindWord = KIND_NAMES[record.kind] || record.kind;
  const kindIcon = KIND_ICONS[record.kind] || 'list';
  const checkboxId = 'zb-select-' + record.id;
  const hint = record.matchHint || {};
  const isAmbiguous = record.kind === 'crm_contact' && hint.how === 'ambiguous';
  const isWaitingProperty = record.kind === 'crm_property' && record.status === 'waiting';
  const canPick = canDecide && (isAmbiguous || isWaitingProperty);

  /* ---- sendDecision(whyText) -- approve or reject, then report ---- */
  async function sendDecision(whyText) {
    setIsBusy(true);
    setAnswerText('');

    let answer = null;

    if (openBox === 'approve') {
      const choiceForServer = choice
        ? (choice.createNew ? { createNew: true } : { chooseCustomerId: choice.chooseCustomerId })
        : null;

      answer = await approveRecord(record.id, whyText, useOlder, choiceForServer);
    } else {
      answer = await rejectRecord(record.id, whyText);
    }

    setIsBusy(false);

    // the server refused because a newer version is already in Butler
    if (answer.status === 409 && answer.message && answer.message.includes('older version')) {
      setNeedsOlderTick(true);
      setAnswerTone('warn');
      setAnswerText(answer.message);
      return;
    }

    if (!answer.ok) {
      setAnswerTone('bad');
      setAnswerText(answer.message || BRIDGE_WORDS.loadFailed);
      return;
    }

    // honest UI: say exactly what the server says happened
    const outcome = answer.data ? answer.data.outcome : '';

    if (outcome === 'failed') {
      setAnswerTone('bad');
      setAnswerText(BRIDGE_WORDS.failedBecause + ' ' + answer.data.message);
    }

    if (outcome === 'waiting') {
      setAnswerTone('warn');
      setAnswerText(answer.data.message);
    }

    setOpenBox('');
    setChoice(null);
    onDecided();
  }

  /* ---- pickCustomer(customer) -- then ask why ---- */
  function pickCustomer(chosenCustomer) {
    setChoice({ chooseCustomerId: chosenCustomer.id, name: chosenCustomer.displayName });
    setOpenBox('approve');
  }

  /* ---- askForNewCustomer() -- then ask why ---- */
  function askForNewCustomer() {
    setChoice({ createNew: true, name: '' });
    setOpenBox('approve');
  }

  const olderTickBox = needsOlderTick ? (
    <label className="zb-check">
      <input type="checkbox" checked={useOlder} onChange={(event) => setUseOlder(event.target.checked)} />
      <span>{BRIDGE_WORDS.useOlderAnyway}</span>
    </label>
  ) : null;

  let approveWord = BRIDGE_WORDS.confirmApprove;

  if (choice && choice.createNew) {
    approveWord = BRIDGE_WORDS.makeNewCustomer;
  } else if (choice) {
    approveWord = BRIDGE_WORDS.useThisCustomer + ': ' + choice.name;
  }

  return (
    <article className="zb-card" aria-labelledby={'zb-title-' + record.id}>
      <header className="zb-card-head">
        {canDecide ? (
          <label className="zb-check zb-check-only" htmlFor={checkboxId}>
            <input
              id={checkboxId}
              type="checkbox"
              checked={isSelected}
              onChange={() => onToggleSelect(record.id)}
            />
            <span className="zb-visually-hidden">Select {record.displayName}</span>
          </label>
        ) : null}
        <div className="zb-card-titles">
          <h3 className="zb-card-title" id={'zb-title-' + record.id}>{record.displayName}</h3>
          <div className="zb-row zb-row-tight">
            <ToneChip tone="neutral" icon={kindIcon} word={kindWord} />
            <StatusChip status={record.status} />
          </div>
        </div>
      </header>

      <div className="zb-card-body">
        <div className="zb-block">
          <h4 className="zb-subhead">{BRIDGE_WORDS.whatZohoSays}</h4>
          <FactList facts={listShortFacts(record)} />
        </div>
        <AllZohoFields record={record} />
        <ChangeList record={record} />
        <MatchLine record={record} />
        <PropertyContacts record={record} />

        {record.status === 'waiting' && record.waitingReason ? (
          <div className="zb-notice zb-notice-warn">
            <BridgeIcon name="clock" />
            <span>{record.waitingReason}</span>
          </div>
        ) : null}

        {record.status === 'failed' && record.failureReason ? (
          <div className="zb-notice zb-notice-bad">
            <BridgeIcon name="alert" />
            <span>{BRIDGE_WORDS.failedBecause} {record.failureReason}</span>
          </div>
        ) : null}

        <p className="zb-small">
          {BRIDGE_WORDS.receivedAt} {formatWhen(record.receivedAt)}
          {record.decidedAt ? ' · ' + BRIDGE_WORDS.decidedBy + ' ' + formatWhen(record.decidedAt) : ''}
          {record.decisionWhy ? ' — "' + record.decisionWhy + '"' : ''}
        </p>
      </div>

      {answerText ? (
        <div className={'zb-notice zb-notice-' + answerTone} role="status">
          <span>{answerText}</span>
        </div>
      ) : null}

      {canDecide && openBox === '' ? (
        <div className="zb-row">
          {canPick ? (
            <button type="button" className="zb-button zb-button-primary" onClick={() => setOpenBox('pick')}>
              <BridgeIcon name="people" />
              <span>{BRIDGE_WORDS.chooseCustomer}</span>
            </button>
          ) : (
            <button type="button" className="zb-button zb-button-primary" onClick={() => setOpenBox('approve')}>
              <BridgeIcon name="check" />
              <span>{BRIDGE_WORDS.approve}</span>
            </button>
          )}
          <button type="button" className="zb-button zb-button-plain" onClick={() => setOpenBox('reject')}>
            <BridgeIcon name="cross" />
            <span>{BRIDGE_WORDS.reject}</span>
          </button>
        </div>
      ) : null}

      {canDecide && openBox === 'pick' ? (
        <>
          <CustomerPicker record={record} onPick={pickCustomer} onMakeNew={askForNewCustomer} />
          <button type="button" className="zb-button zb-button-plain" onClick={() => setOpenBox('')}>
            {BRIDGE_WORDS.cancel}
          </button>
        </>
      ) : null}

      {canDecide && (openBox === 'approve' || openBox === 'reject') ? (
        <WhyBox
          confirmWord={openBox === 'approve' ? approveWord : BRIDGE_WORDS.confirmReject}
          tone={openBox === 'approve' ? 'primary' : 'danger'}
          busy={isBusy}
          extra={openBox === 'approve' ? olderTickBox : null}
          onConfirm={sendDecision}
          onCancel={() => {
            setOpenBox('');
            setChoice(null);
          }}
        />
      ) : null}
    </article>
  );
}
